import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { adminAPI, modulesAPI } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import RichTextEditor from "@/components/RichTextEditor";
import {
  Users,
  BookOpen,
  BarChart3,
  Plus,
  Trash2,
  Edit,
  Check,
  X,
  UserPlus,
  ShieldCheck,
  Clock,
  Eye,
  EyeOff,
  Loader2,
  Save,
  TrendingUp,
  Star,
  FileText,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_exec-talent-hub-1/artifacts/7a127wcx_Color%20logo%20-%20no%20background.svg";

const LEVEL_TITLES = { 1: "Associate", 2: "Consultant", 3: "Senior Consultant", 4: "Principal", 5: "Partner" };
const READINESS_OPTIONS = ["learning", "shadowing", "client_ready", "fully_billable"];
const READINESS_LABELS = { learning: "Learning", shadowing: "Shadowing", client_ready: "Client-Ready", fully_billable: "Fully Billable" };
const READINESS_COLORS = { learning: "bg-slate-100 text-slate-700", shadowing: "bg-sky-50 text-sky-700", client_ready: "bg-amber-50 text-amber-700", fully_billable: "bg-emerald-50 text-emerald-700" };

function formatTime(seconds) {
  if (!seconds || seconds === 0) return "0m";
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (hrs > 0) return `${hrs}h ${mins}m`;
  return `${mins}m`;
}

// ======= USER MANAGEMENT TAB =======
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", password: "", full_name: "", role: "user" });
  const [creating, setCreating] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editData, setEditData] = useState({});

  const loadUsers = useCallback(async () => {
    try {
      const res = await adminAPI.getUsers();
      setUsers(res.data);
    } catch (err) {
      console.error("Failed to load users:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await adminAPI.createUser(newUser);
      setNewUser({ username: "", password: "", full_name: "", role: "user" });
      setShowCreate(false);
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (username, currentActive) => {
    try {
      await adminAPI.updateUser(username, { is_active: !currentActive });
      loadUsers();
    } catch (err) {
      alert("Failed to update user");
    }
  };

  const handleResetPassword = async (username) => {
    const newPassword = prompt(`Enter new password for ${username}:`);
    if (!newPassword) return;
    try {
      await adminAPI.updateUser(username, { password: newPassword });
      alert("Password reset successfully");
    } catch (err) {
      alert("Failed to reset password");
    }
  };

  const handleUpdateUser = async (username) => {
    try {
      await adminAPI.updateUser(username, editData);
      setEditingUser(null);
      loadUsers();
    } catch (err) {
      alert("Failed to update user");
    }
  };

  if (loading) return <div className="text-sm text-slate-400 py-8 text-center">Loading users...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold text-slate-900">User Accounts</h3>
        <Button
          data-testid="create-user-btn"
          onClick={() => setShowCreate(!showCreate)}
          className="bg-[#005d6c] hover:bg-[#004a56] text-white rounded-sm text-sm"
        >
          <UserPlus className="w-3.5 h-3.5 mr-2" />
          Create User
        </Button>
      </div>

      {/* Create User Form */}
      {showCreate && (
        <form onSubmit={handleCreateUser} className="bg-white border border-slate-200 rounded-sm p-5 shadow-sm space-y-4" data-testid="create-user-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Username</Label>
              <Input
                data-testid="new-user-username"
                value={newUser.username}
                onChange={(e) => setNewUser(p => ({ ...p, username: e.target.value }))}
                placeholder="username"
                required
                className="rounded-sm h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Password</Label>
              <Input
                data-testid="new-user-password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser(p => ({ ...p, password: e.target.value }))}
                placeholder="password"
                required
                className="rounded-sm h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Full Name</Label>
              <Input
                data-testid="new-user-fullname"
                value={newUser.full_name}
                onChange={(e) => setNewUser(p => ({ ...p, full_name: e.target.value }))}
                placeholder="Full Name"
                required
                className="rounded-sm h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Role</Label>
              <select
                data-testid="new-user-role"
                value={newUser.role}
                onChange={(e) => setNewUser(p => ({ ...p, role: e.target.value }))}
                className="w-full h-9 rounded-sm border border-slate-200 px-3 text-sm"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button type="submit" disabled={creating} className="bg-[#005d6c] hover:bg-[#004a56] text-white rounded-sm text-sm">
              {creating ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" />Creating...</> : "Create User"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setShowCreate(false)} className="text-sm">Cancel</Button>
          </div>
        </form>
      )}

      {/* Users Table */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 font-semibold text-slate-700">User</th>
                <th className="text-left px-3 py-3 font-semibold text-slate-700">Role</th>
                <th className="text-center px-3 py-3 font-semibold text-slate-700">Level</th>
                <th className="text-center px-3 py-3 font-semibold text-slate-700">Readiness</th>
                <th className="text-center px-3 py-3 font-semibold text-slate-700">Progress</th>
                <th className="text-center px-3 py-3 font-semibold text-slate-700">Status</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.username} data-testid={`user-row-${u.username}`} className="border-b border-slate-100">
                  <td className="px-4 py-3">
                    {editingUser === u.username ? (
                      <Input
                        value={editData.full_name ?? u.full_name}
                        onChange={(e) => setEditData(p => ({ ...p, full_name: e.target.value }))}
                        className="h-8 text-sm rounded-sm w-40"
                      />
                    ) : (
                      <div>
                        <div className="font-medium text-slate-900">{u.full_name || u.username}</div>
                        <div className="text-xs text-slate-400">{u.username}</div>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <Badge variant="secondary" className={`text-xs ${u.role === 'admin' ? 'bg-[#005d6c] text-white' : ''}`}>
                      {u.role}
                    </Badge>
                  </td>
                  <td className="text-center px-3 py-3">
                    <select
                      value={u.promotion_level || 1}
                      onChange={async (e) => {
                        try {
                          await adminAPI.updatePromotion(u.username, parseInt(e.target.value));
                          loadUsers();
                        } catch (err) { alert("Failed to update level"); }
                      }}
                      className="h-7 rounded-sm border border-slate-200 px-1.5 text-xs w-full max-w-[120px]"
                      data-testid={`promotion-select-${u.username}`}
                    >
                      {Object.entries(LEVEL_TITLES).map(([k, v]) => (
                        <option key={k} value={k}>{`L${k}: ${v}`}</option>
                      ))}
                    </select>
                  </td>
                  <td className="text-center px-3 py-3">
                    <select
                      value={u.revenue_readiness || "learning"}
                      onChange={async (e) => {
                        try {
                          await adminAPI.updateReadiness(u.username, e.target.value);
                          loadUsers();
                        } catch (err) { alert("Failed to update readiness"); }
                      }}
                      className="h-7 rounded-sm border border-slate-200 px-1.5 text-xs w-full max-w-[110px]"
                      data-testid={`readiness-select-${u.username}`}
                    >
                      {READINESS_OPTIONS.map(r => (
                        <option key={r} value={r}>{READINESS_LABELS[r]}</option>
                      ))}
                    </select>
                  </td>
                  <td className="text-center px-3 py-3">
                    <span className="text-sm font-medium">{u.completed_modules || 0}/20</span>
                  </td>
                  <td className="text-center px-3 py-3">
                    <Badge variant="secondary" className={`text-xs ${u.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                      {u.is_active ? "Active" : "Disabled"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {editingUser === u.username ? (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => handleUpdateUser(u.username)} className="h-7 w-7 p-0">
                            <Check className="w-3.5 h-3.5 text-emerald-600" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingUser(null)} className="h-7 w-7 p-0">
                            <X className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => { setEditingUser(u.username); setEditData({ full_name: u.full_name }); }} className="h-7 w-7 p-0" title="Edit">
                            <Edit className="w-3.5 h-3.5 text-slate-500" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleToggleActive(u.username, u.is_active)} className="h-7 w-7 p-0" title={u.is_active ? "Disable" : "Enable"}>
                            {u.is_active ? <EyeOff className="w-3.5 h-3.5 text-slate-500" /> : <Eye className="w-3.5 h-3.5 text-slate-500" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleResetPassword(u.username)} className="h-7 w-7 p-0" title="Reset Password">
                            <ShieldCheck className="w-3.5 h-3.5 text-slate-500" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ======= CONTENT MANAGEMENT TAB =======
function ContentTab() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [newModule, setNewModule] = useState({ title: "", description: "", icon: "BookOpen" });
  const [saving, setSaving] = useState(false);
  const [contentEditModule, setContentEditModule] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  const loadModules = useCallback(async () => {
    try {
      const res = await modulesAPI.getAll();
      setModules(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadModules(); }, [loadModules]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminAPI.createModule(newModule);
      setShowCreate(false);
      setNewModule({ title: "", description: "", icon: "BookOpen" });
      loadModules();
    } catch (err) {
      alert("Failed to create module");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    setSaving(true);
    try {
      await adminAPI.updateModule(id, editData);
      setEditingId(null);
      loadModules();
    } catch (err) {
      alert("Failed to update module");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this module? This cannot be undone.")) return;
    try {
      await adminAPI.deleteModule(id);
      loadModules();
    } catch (err) {
      alert("Failed to delete module");
    }
  };

  const handleOpenContentEditor = async (mod) => {
    // Fetch the full module data including learning content
    try {
      const res = await modulesAPI.getOne(mod.id);
      const fullModule = res.data;
      setContentEditModule({
        ...fullModule,
        learning: fullModule.learning || { sections: [] }
      });
      setExpandedSection(null);
    } catch (err) {
      console.error("Failed to load module content:", err);
      alert("Failed to load module content");
    }
  };

  const handleSectionContentChange = (sectionIndex, newContent) => {
    setContentEditModule(prev => {
      const updatedSections = [...(prev.learning?.sections || [])];
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        content: newContent
      };
      return {
        ...prev,
        learning: { ...prev.learning, sections: updatedSections }
      };
    });
  };

  const handleSectionTitleChange = (sectionIndex, newTitle) => {
    setContentEditModule(prev => {
      const updatedSections = [...(prev.learning?.sections || [])];
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        title: newTitle
      };
      return {
        ...prev,
        learning: { ...prev.learning, sections: updatedSections }
      };
    });
  };

  const handleAddSection = () => {
    setContentEditModule(prev => ({
      ...prev,
      learning: {
        ...prev.learning,
        sections: [...(prev.learning?.sections || []), { title: "New Section", content: "" }]
      }
    }));
  };

  const handleDeleteSection = (sectionIndex) => {
    if (!window.confirm("Delete this section?")) return;
    setContentEditModule(prev => {
      const updatedSections = (prev.learning?.sections || []).filter((_, i) => i !== sectionIndex);
      return {
        ...prev,
        learning: { ...prev.learning, sections: updatedSections }
      };
    });
  };

  const handleSaveContent = async () => {
    if (!contentEditModule) return;
    setSaving(true);
    try {
      await adminAPI.updateModule(contentEditModule.id, {
        learning: contentEditModule.learning
      });
      loadModules();
      setContentEditModule(null);
      alert("Content saved successfully!");
    } catch (err) {
      alert("Failed to save content");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-400 py-8 text-center">Loading modules...</div>;

  // Content Editor View
  if (contentEditModule) {
    return (
      <div className="space-y-4" data-testid="content-editor-view">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              onClick={() => setContentEditModule(null)} 
              className="h-8 px-2"
              data-testid="back-to-modules-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h3 className="font-heading text-lg font-semibold text-slate-900">
              Editing: Module {contentEditModule.id} — {contentEditModule.title}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleAddSection}
              variant="outline"
              className="rounded-sm text-sm"
              data-testid="add-section-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Section
            </Button>
            <Button
              onClick={handleSaveContent}
              disabled={saving}
              className="bg-[#005d6c] hover:bg-[#004a56] text-white rounded-sm text-sm"
              data-testid="save-content-btn"
            >
              {saving ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Saving...</> : <><Save className="w-3 h-3 mr-1.5" />Save All</>}
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          {(contentEditModule.learning?.sections || []).map((section, idx) => (
            <div key={idx} className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid={`content-section-${idx}`}>
              <div 
                className="flex items-center justify-between p-4 cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors"
                onClick={() => setExpandedSection(expandedSection === idx ? null : idx)}
              >
                <div className="flex items-center gap-3 flex-1">
                  <span className="w-6 h-6 rounded-sm bg-[#005d6c] text-white flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                  {expandedSection === idx ? (
                    <Input
                      value={section.title}
                      onChange={(e) => { e.stopPropagation(); handleSectionTitleChange(idx, e.target.value); }}
                      onClick={(e) => e.stopPropagation()}
                      className="h-8 text-sm font-medium flex-1 max-w-md"
                      data-testid={`section-title-input-${idx}`}
                    />
                  ) : (
                    <span className="text-sm font-medium text-slate-800">{section.title}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => { e.stopPropagation(); handleDeleteSection(idx); }}
                    className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                    data-testid={`delete-section-btn-${idx}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                  {expandedSection === idx ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </div>
              {expandedSection === idx && (
                <div className="p-4 border-t border-slate-200">
                  <Label className="text-xs font-medium text-slate-500 mb-2 block">Section Content (Rich Text)</Label>
                  <RichTextEditor
                    content={section.content || ""}
                    onChange={(value) => handleSectionContentChange(idx, value)}
                    placeholder="Start writing section content... Supports tables, lists, formatting, and more."
                  />
                </div>
              )}
            </div>
          ))}
          {(!contentEditModule.learning?.sections || contentEditModule.learning.sections.length === 0) && (
            <div className="text-center py-8 text-slate-400 text-sm">
              No learning sections yet. Click "Add Section" to create one.
            </div>
          )}
        </div>
      </div>
    );
  }

  // Module List View
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold text-slate-900">Module Content</h3>
        <Button
          data-testid="create-module-btn"
          onClick={() => setShowCreate(!showCreate)}
          className="bg-[#005d6c] hover:bg-[#004a56] text-white rounded-sm text-sm"
        >
          <Plus className="w-3.5 h-3.5 mr-2" />
          New Module
        </Button>
      </div>

      {showCreate && (
        <form onSubmit={handleCreate} className="bg-white border border-slate-200 rounded-sm p-5 shadow-sm space-y-4" data-testid="create-module-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs font-medium">Title</Label>
              <Input
                data-testid="new-module-title"
                value={newModule.title}
                onChange={(e) => setNewModule(p => ({ ...p, title: e.target.value }))}
                placeholder="Module title"
                required
                className="rounded-sm h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs font-medium">Description</Label>
              <textarea
                data-testid="new-module-description"
                value={newModule.description}
                onChange={(e) => setNewModule(p => ({ ...p, description: e.target.value }))}
                placeholder="Module description"
                rows={3}
                required
                className="w-full rounded-sm border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-900"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button type="submit" disabled={saving} className="bg-[#005d6c] hover:bg-[#004a56] text-white rounded-sm text-sm">
              {saving ? "Creating..." : "Create Module"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setShowCreate(false)} className="text-sm">Cancel</Button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {modules.map((mod) => (
          <div key={mod.id} data-testid={`admin-module-${mod.id}`} className="bg-white border border-slate-200 rounded-sm p-4 shadow-sm">
            {editingId === mod.id ? (
              <div className="space-y-3">
                <Input
                  value={editData.title ?? mod.title}
                  onChange={(e) => setEditData(p => ({ ...p, title: e.target.value }))}
                  className="rounded-sm h-9 text-sm font-medium"
                />
                <textarea
                  value={editData.description ?? mod.description}
                  onChange={(e) => setEditData(p => ({ ...p, description: e.target.value }))}
                  rows={2}
                  className="w-full rounded-sm border border-slate-200 px-3 py-2 text-sm"
                />
                <div className="flex items-center gap-2">
                  <Button onClick={() => handleUpdate(mod.id)} disabled={saving} className="bg-[#005d6c] hover:bg-[#004a56] text-white rounded-sm text-sm">
                    <Save className="w-3 h-3 mr-1.5" />{saving ? "Saving..." : "Save"}
                  </Button>
                  <Button variant="ghost" onClick={() => setEditingId(null)} className="text-sm">Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-7 h-7 rounded-sm bg-slate-100 text-xs font-bold text-slate-600">{mod.id}</span>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900">{mod.title}</h4>
                    <p className="text-xs text-slate-500 line-clamp-1">{mod.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => handleOpenContentEditor(mod)} 
                    className="h-7 px-2 text-[#005d6c] hover:bg-[#005d6c]/10"
                    data-testid={`edit-content-btn-${mod.id}`}
                    title="Edit Learning Content"
                  >
                    <FileText className="w-3.5 h-3.5 mr-1" />
                    <span className="text-xs">Content</span>
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditingId(mod.id); setEditData({ title: mod.title, description: mod.description }); }} className="h-7 w-7 p-0" title="Edit Title/Description">
                    <Edit className="w-3.5 h-3.5 text-slate-500" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(mod.id)} className="h-7 w-7 p-0" title="Delete Module">
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ======= ANALYTICS TAB =======
const BD_KPI_LABELS = {
  learning_progress: "Learning Progress %",
  research_tasks_completed: "Research Tasks",
  outreach_shadowed: "Outreach Shadowed",
  client_meetings_booked: "Client Meetings Booked",
  flip_calls_conducted: "Flip Calls",
  spec_attempts: "Spec Attempts",
  mandates_supported: "Mandates Supported",
  new_client_meetings_won: "New Client Meetings",
  mandates_closed: "Mandates Closed",
  revenue_generated: "Revenue Generated ($)",
  desk_pipeline_strength: "Pipeline Strength",
  repeat_business_pct: "Repeat Business %",
  account_expansion: "Account Expansion",
  team_contribution: "Team Contribution",
  revenue_consistency: "Revenue Consistency ($)",
  client_origination: "Client Origination",
  total_book_value: "Total Book Value ($)",
  team_revenue_contribution: "Team Revenue ($)",
  brand_visibility_score: "Brand Visibility",
};

function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kpiThresholds, setKpiThresholds] = useState(null);
  const [showKPIs, setShowKPIs] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [analyticsRes, kpiRes] = await Promise.all([
          adminAPI.getAnalytics(),
          adminAPI.getKPIThresholds()
        ]);
        setData(analyticsRes.data);
        setKpiThresholds(kpiRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <div className="text-sm text-slate-400 py-8 text-center">Loading analytics...</div>;
  if (!data) return null;

  const MODULE_TITLES = [
    "Foundations", "Client Acquisition", "Role Intake", "Research & Mapping",
    "Candidate ID", "Evaluation", "Presentation", "Interview Mgmt",
    "Offer Negotiation", "Post-Placement", "Mindset & Ethics",
    "BD Fundamentals", "Getting Meetings", "Flip Calling", "Spec Strategy",
    "Pitching", "Account Mgmt", "Desk Building", "Personal Brand", "Operating System"
  ];

  const LEVEL_TITLES = { 1: "Associate", 2: "Consultant", 3: "Senior Consultant", 4: "Principal", 5: "Partner" };
  const moduleIds = Object.keys(data.module_stats).sort((a, b) => parseInt(a) - parseInt(b));

  const formatKPIValue = (key, value) => {
    if (key.includes("revenue") || key.includes("book_value")) return `$${(value/1000).toFixed(0)}K`;
    if (key.includes("pct") || key === "brand_visibility_score" || key === "learning_progress") return `${value}%`;
    return value;
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-sm p-5 shadow-sm">
          <div className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-1">Total Users</div>
          <div className="text-2xl font-bold text-slate-900 font-heading">{data.total_users}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-5 shadow-sm">
          <div className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-1">Active Users</div>
          <div className="text-2xl font-bold text-slate-900 font-heading">{data.active_users}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-5 shadow-sm">
          <div className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-1">Total Assessments</div>
          <div className="text-2xl font-bold text-slate-900 font-heading">
            {data.user_analytics.reduce((sum, u) => sum + u.assessment_attempts, 0)}
          </div>
        </div>
      </div>

      {/* BD KPI Framework */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#005d6c]" />
            BD KPI Framework by Promotion Level
          </h3>
          <Button
            variant="ghost"
            onClick={() => setShowKPIs(!showKPIs)}
            className="text-sm"
            data-testid="toggle-kpis-btn"
          >
            {showKPIs ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
            {showKPIs ? "Hide" : "Show"} KPIs
          </Button>
        </div>
        
        {showKPIs && kpiThresholds && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="kpi-framework">
            {Object.entries(kpiThresholds).map(([level, kpis]) => (
              <div key={level} className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid={`kpi-level-${level}`}>
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                  <Star className="w-4 h-4 text-[#005d6c]" />
                  <span className="text-sm font-semibold text-slate-800">Level {level}: {LEVEL_TITLES[parseInt(level)]}</span>
                </div>
                <div className="p-4 space-y-2">
                  {Object.entries(kpis || {}).map(([kpiKey, kpiValue]) => (
                    <div key={kpiKey} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{BD_KPI_LABELS[kpiKey] || kpiKey}</span>
                      <span className="font-medium text-slate-900">{formatKPIValue(kpiKey, kpiValue)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {!showKPIs && (
          <p className="text-xs text-slate-400">Click "Show KPIs" to view the BD Key Performance Indicators required at each promotion level.</p>
        )}
      </div>

      {/* Module Completion Rates */}
      <div>
        <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">Module Completion Rates</h3>
        <div className="bg-white border border-slate-200 rounded-sm p-5 shadow-sm space-y-3">
          {moduleIds.map((modId) => {
            const i = parseInt(modId) - 1;
            const stats = data.module_stats[modId] || { started: 0, completed: 0 };
            const totalUsers = data.total_users || 1;
            const completionRate = Math.round((stats.completed / totalUsers) * 100);
            const startRate = Math.round((stats.started / totalUsers) * 100);
            return (
              <div key={modId} data-testid={`analytics-module-${modId}`} className="flex items-center gap-4">
                <span className="text-xs text-slate-400 w-6 text-right font-medium">{modId}</span>
                <span className="text-sm text-slate-700 w-36 truncate">{MODULE_TITLES[i] || `Module ${modId}`}</span>
                <div className="flex-1">
                  <div className="h-4 bg-slate-100 rounded-sm overflow-hidden relative">
                    <div className="absolute inset-0 bg-slate-200 rounded-sm" style={{ width: `${startRate}%` }} />
                    <div className="absolute inset-0 bg-[#005d6c] rounded-sm" style={{ width: `${completionRate}%` }} />
                  </div>
                </div>
                <span className="text-xs text-slate-500 w-16 text-right">
                  {stats.completed}/{totalUsers}
                </span>
                <span className="text-xs font-medium text-slate-700 w-10 text-right">{completionRate}%</span>
              </div>
            );
          })}
          <div className="flex items-center gap-3 pt-2 text-xs text-slate-500">
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-slate-200 rounded-sm" /> Started</div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-[#005d6c] rounded-sm" /> Completed</div>
          </div>
        </div>
      </div>

      {/* Per-User Analytics */}
      <div>
        <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">User Progress & Time Spent</h3>
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">User</th>
                  <th className="text-center px-3 py-3 font-semibold text-slate-700">Modules</th>
                  <th className="text-center px-3 py-3 font-semibold text-slate-700">Total Time</th>
                  <th className="text-center px-3 py-3 font-semibold text-slate-700">Assessments</th>
                  <th className="text-left px-3 py-3 font-semibold text-slate-700">Time Per Module</th>
                </tr>
              </thead>
              <tbody>
                {data.user_analytics.map((u) => (
                  <tr key={u.username} data-testid={`analytics-user-${u.username}`} className="border-b border-slate-100">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{u.full_name || u.username}</div>
                      <div className="text-xs text-slate-400">{u.username}</div>
                    </td>
                    <td className="text-center px-3 py-3">
                      <span className="font-medium">{u.completed_modules}</span>
                      <span className="text-slate-400">/20</span>
                    </td>
                    <td className="text-center px-3 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <Clock className="w-3 h-3 text-slate-400" />
                        <span className="font-medium">{formatTime(u.total_time_seconds)}</span>
                      </div>
                    </td>
                    <td className="text-center px-3 py-3 font-medium">{u.assessment_attempts}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1 flex-wrap">
                        {moduleIds.map((mid) => {
                          const time = u.module_times[mid] || 0;
                          return (
                            <div
                              key={mid}
                              title={`M${parseInt(mid)}: ${formatTime(time)}`}
                              className="w-5 h-5 rounded-sm flex items-center justify-center text-[8px] font-medium"
                              style={{
                                backgroundColor: time > 1800 ? '#005d6c' : time > 600 ? '#94a3b8' : time > 0 ? '#e2e8f0' : '#f8fafc',
                                color: time > 1800 ? '#fff' : time > 600 ? '#fff' : '#94a3b8',
                                border: '1px solid #e2e8f0'
                              }}
                            >
                              {parseInt(mid)}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-[#f8fafc] border border-slate-200 rounded-sm" /> No time</div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-[#e2e8f0] rounded-sm" /> &lt;10m</div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-[#94a3b8] rounded-sm" /> 10-30m</div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-[#005d6c] rounded-sm" /> 30m+</div>
        </div>
      </div>
    </div>
  );
}

// ======= MAIN ADMIN PAGE =======
export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (user?.role !== "admin") {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500 text-sm">You don't have permission to access this page.</p>
        <Button onClick={() => navigate("/")} variant="ghost" className="mt-4 text-sm">
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex items-center gap-4">
        <img src={LOGO_URL} alt="Axentia Global" className="h-6" />
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            Admin Panel
          </h1>
          <p className="text-slate-500 text-sm font-body">Manage users, content, and view analytics.</p>
        </div>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="bg-slate-100 rounded-sm h-10 p-1">
          <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-sm text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2">
            <Users className="w-3.5 h-3.5" />
            Users
          </TabsTrigger>
          <TabsTrigger value="content" data-testid="admin-tab-content" className="rounded-sm text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2">
            <BookOpen className="w-3.5 h-3.5" />
            Content
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics" className="rounded-sm text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2">
            <BarChart3 className="w-3.5 h-3.5" />
            Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6">
          <UsersTab />
        </TabsContent>
        <TabsContent value="content" className="mt-6">
          <ContentTab />
        </TabsContent>
        <TabsContent value="analytics" className="mt-6">
          <AnalyticsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
